import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';

// material-ui
import {useTheme} from '@mui/material/styles';
import {Alert, Box, Card, Divider, Grid, IconButton, Tooltip, Typography} from '@mui/material';

// project imports
import {BASE_PATH} from 'config';
import {gridSpacing} from 'store/constant';

// assets
import {IconTallymark1} from '@tabler/icons';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import {NavItemType, NavItemTypeObject, OverrideIcon} from 'types';
import useConfig from "../../hooks/useConfig";
import {Company} from "../../types/type/activities-types";
import {CachedTwoTone, InfoTwoTone} from "@mui/icons-material";
import {format} from "date-fns";
import {useIntl} from "react-intl";

const linkSX = {
    display: 'flex',
    color: 'grey.900',
    textDecoration: 'none',
    alignContent: 'center',
    alignItems: 'center'
};

interface BreadCrumbSxProps extends React.CSSProperties {
    mb?: string;
    bgcolor?: string;
}

interface BreadCrumbsProps {
    card?: boolean;
    divider?: boolean;
    icon?: boolean;
    icons?: boolean;
    maxItems?: number;
    navigation?: NavItemTypeObject;
    rightAlign?: boolean;
    separator?: OverrideIcon;
    title?: boolean;
    titleBottom?: boolean;
    sx?: BreadCrumbSxProps;
    showCompanyField?: boolean;
    companiesArray: Company[];
    lastConsolidate: any[];
}

// ==============================|| BREADCRUMBS ||============================== //

const Breadcrumbs = ({
                         card,
                         divider,
                         icon,
                         icons,
                         maxItems,
                         navigation,
                         rightAlign,
                         separator,
                         title,
                         titleBottom,
                         showCompanyField,
                         companiesArray,
                         lastConsolidate,
                         ...others
                     }: BreadCrumbsProps) => {
    const theme = useTheme();
    const {onChangeCompany, company, onChangeReloadPage, reloadPage} = useConfig();
    const intl = useIntl()

    const iconStyle = {
        marginRight: theme.spacing(0.75),
        marginTop: `-${theme.spacing(0.25)}`,
        width: '16px',
        height: '16px',
        color: theme.palette.secondary.main
    };

    const [main, setMain] = useState<NavItemType | undefined>();
    const [item, setItem] = useState<NavItemType>();

    useEffect(() => {
        navigation?.items?.map((menu: NavItemType | NavItemTypeObject, index: number) => {
            if (menu.type && menu.type === 'group') {
                getCollapse(menu as { children: NavItemType[]; type?: string });
            }
            return false;
        });
    });

    // set active item state
    const getCollapse = (menu: NavItemTypeObject) => {
        if (menu.children) {
            menu.children.filter((collapse: NavItemType) => {
                if (collapse.type && collapse.type === 'collapse') {
                    getCollapse(collapse as { children: NavItemType[]; type?: string });
                } else if (collapse.type && collapse.type === 'item') {
                    if (document.location.pathname === BASE_PATH + collapse.url) {
                        setMain(menu);
                        setItem(collapse);
                    }
                }
                return false;
            });
        }
    };

    // item separator
    const SeparatorIcon = separator!;
    const separatorIcon = separator ? <SeparatorIcon stroke={1.5} size="16px"/> :
        <IconTallymark1 stroke={1.5} size="16px"/>;

    let mainContent;
    let itemContent;
    let breadcrumbContent: React.ReactElement = <Typography/>;
    let itemTitle: NavItemType['title'] = '';
    let CollapseIcon;
    let ItemIcon;

    // collapse item
    if (main && main.type === 'collapse') {
        CollapseIcon = main.icon ? main.icon : AccountTreeTwoToneIcon;
        mainContent = (
            <Typography component={Link} to="#" variant="subtitle1" sx={linkSX}>
                {icons && <CollapseIcon style={iconStyle}/>}
                {main.title}
            </Typography>
        );
    }

    const lastConsolidateArray = () => {
        let content = (
            <>
                {
                    lastConsolidate.map((el) => {
                        let companyRecord = companiesArray.find((sn) => sn.id == el.location_id)
                        return <>{companyRecord?.name}: {format(new Date(el.last_closure), 'dd/MM/yyyy HH:mm')}<br/></>
                    })
                }
            </>
        );
        return content
    }

    // items
    if (item && item.type === 'item') {
        itemTitle = item.title;

        ItemIcon = item.icon ? item.icon : AccountTreeTwoToneIcon;
        itemContent = (
            <Typography
                variant="subtitle1"
                sx={{
                    display: 'flex',
                    textDecoration: 'none',
                    alignContent: 'center',
                    alignItems: 'center',
                    color: 'grey.500'
                }}
            >
                {icons && <ItemIcon style={iconStyle}/>}
                {itemTitle}
            </Typography>
        );

        // main
        if (item.breadcrumbs !== false) {
            breadcrumbContent = (
                <Card
                    sx={{
                        marginBottom: card === false ? 0 : theme.spacing(gridSpacing),
                        border: card === false ? 'none' : '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary[200] + 75,
                        background: card === false ? 'transparent' : theme.palette.background.default
                    }}
                    {...others}
                >
                    <Box sx={{p: 1, pl: card === false ? 0 : 2}}>
                        <Grid
                            container
                            direction={rightAlign ? 'row' : 'column'}
                            justifyContent={rightAlign ? 'space-between' : 'flex-start'}
                            alignItems={rightAlign ? 'center' : 'flex-start'}
                            spacing={1}
                        >
                            {title && !titleBottom && (
                                <>
                                    <Grid item>
                                        <Typography variant="h3" sx={{fontWeight: 500}}>
                                            {item.title}
                                            <Tooltip placement="top" title={intl.formatMessage({id: 'SYS00027'})}>
                                                <IconButton
                                                    color="secondary"
                                                    style={{marginLeft: '16px'}}
                                                    onClick={() => {
                                                        onChangeReloadPage(!reloadPage)
                                                    }}
                                                >
                                                    <CachedTwoTone sx={{fontSize: '1.4rem'}}/>
                                                </IconButton>
                                            </Tooltip>
                                        </Typography>
                                    </Grid>
                                </>
                            )}
                            <Grid item>
                                <Tooltip
                                    placement={'left'}
                                    title={lastConsolidateArray()}>
                                    <Alert severity={'info'}
                                           icon={<InfoTwoTone style={{fontSize: '0.85em'}}/>}>Stato
                                        consolidamenti</Alert>
                                </Tooltip>
                            </Grid>
                            {title && titleBottom && (
                                <Grid item>
                                    <Typography variant="h3" sx={{fontWeight: 500}}>
                                        {item.title}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    {card === false && divider !== false &&
                        <Divider sx={{borderColor: theme.palette.primary.main, mb: gridSpacing}}/>}
                </Card>
            );
        }
    }

    return breadcrumbContent;
};

export default Breadcrumbs;